import React, { useState, useEffect, useCallback } from 'react';
import { deployAPI, cloudAPI, wakeGatewayAPI } from '../services/api';

const PROVIDERS = ['aws','gcp','azure','digitalocean'];
const INSTANCE_TYPES = {
  aws         : ['t3.micro','t3.small','t3.medium','t3.large'],
  gcp         : ['e2-micro','e2-small','e2-medium','e2-standard'],
  azure       : ['B1s','B1ms','B2s','B2ms'],
  digitalocean: ['s-1vcpu-1gb','s-1vcpu-2gb','s-2vcpu-2gb','s-2vcpu-4gb'],
};
const HOST_IP = '168.144.184.59';
const PROVIDER_COLORS = { aws:'#ff9900', gcp:'#4285f4', azure:'#0078d4', digitalocean:'#0080ff', vultr:'#007bfc', lightnode:'#00c853' };
const STATUS_BADGE = { running:'badge-green', stopped:'badge-yellow', failed:'badge-red', provisioning:'badge-blue', terminated:'badge-purple', pending:'badge-blue' };
const EMPTY_FORM = { name:'', provider:'gcp', region:'us-central1', type:'compute', instanceType:'e2-micro', ephemeral:false };

export default function Deployments() {
  const [deployments, setDeployments] = useState([]);
  const [wakeGateways, setWakeGateways] = useState([]);
  const [regions,     setRegions]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showModal,   setShowModal]   = useState(false);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [submitting,  setSubmitting]  = useState(false);
  const [filter,      setFilter]      = useState('all');
  const [toast,       setToast]       = useState(null);

  const showToast = (msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 1000, ...(filter !== 'all' ? { status: filter } : {}) };
    deployAPI.list(params)
      .then(r => setDeployments(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter]);
  const loadWakeGateways = useCallback(() => {
    wakeGatewayAPI.list().then(r => setWakeGateways(r.data.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadWakeGateways(); }, [loadWakeGateways]);

  // Poll every 30 seconds instead of 10
  useEffect(() => {
    load();
    const interval = setInterval(() => load(), 30000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    cloudAPI.regions(form.provider)
      .then(r => {
        const regs = r.data.data || [];
        setRegions(regs);
        setForm(f => ({ ...f, region: regs[0] || '', instanceType: (INSTANCE_TYPES[form.provider] || [])[0] || '' }));
      })
      .catch(() => setRegions([]));
  }, [form.provider]);

  const handleCreate = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await deployAPI.create({ ...form, config: { instanceType: form.instanceType } });
      if (r.data?.success) {
        showToast(r.data.message || 'Deployment created successfully!');
        setShowModal(false);
        setForm(EMPTY_FORM);
        setTimeout(load, 3000);
        setTimeout(load, 10000);
      } else {
        showToast(r.data?.error || 'Deployment failed', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.error || err.message || 'Failed to create deployment', 'error');
    } finally { setSubmitting(false); }
  };

  const handleStop = async id => {
    try {
      await deployAPI.stop(id);
      showToast('Stop initiated — reflecting in ~30s');
      load();
      setTimeout(() => load(), 10000);
      setTimeout(() => load(), 25000);
    } catch { showToast('Failed to stop deployment', 'error'); }
  };
  const handleToggleAutoStop = async (id, enabled) => {
    try {
      await fetch(`/api/deployments/${id}/auto-stop`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('mcp_token') },
        body: JSON.stringify({ enabled }),
      });
      showToast(enabled ? 'Auto-stop enabled' : 'Auto-stop disabled');
      load();
    } catch { showToast('Failed to update auto-stop', 'error'); }
  };
  const handleToggleWake = async (dep, enabled) => {
    try {
      if (enabled) {
        await wakeGatewayAPI.create({ deploymentId: dep._id, targetPort: 22 });
        showToast('Wake-on-connect enabled');
      } else {
        const gw = wakeGateways.find(g => g.deploymentId?._id === dep._id || g.deploymentId === dep._id);
        if (gw) await wakeGatewayAPI.remove(gw._id);
        showToast('Wake-on-connect disabled');
      }
      loadWakeGateways();
    } catch { showToast('Failed to update wake-on-connect', 'error'); }
  };

  const handleStart = async id => {
    try {
      await deployAPI.start(id);
      showToast('Start initiated — reflecting in ~30s');
      load();
      setTimeout(() => load(), 10000);
      setTimeout(() => load(), 25000);
    } catch { showToast('Failed to start deployment', 'error'); }
  };

  const handleDestroy = async id => {
    if (!window.confirm('Terminate this deployment? This cannot be undone.')) return;
    try { await deployAPI.destroy(id); showToast('Deployment terminated'); load(); }
    catch { showToast('Failed to terminate', 'error'); }
  };

  return (
    <div>
      {toast && (
        <div className="toast-container">
          <div className={`toast ${toast.type}`}>
            {toast.type === 'success' ? '✅' : '❌'} {toast.msg}
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1>Deployments</h1>
          <p>Manage your multi-cloud workloads</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Deployment</button>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {['all','running','stopped','failed','terminated'].map(f => (
          <button key={f} className={`btn btn-sm ${filter===f ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter(f)} style={{ textTransform:'capitalize' }}>{f}</button>
        ))}
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : deployments.length === 0 ? (
          <div className="empty">
            <div className="empty-icon">🚀</div>
            <h3>No deployments found</h3>
            <p>Create your first deployment to get started</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Name</th><th>Provider</th><th>Region</th><th>Type</th>
                <th>Status</th><th>Public IP</th><th>Cost/hr</th><th>Total Cost</th><th>Auto-Stop</th><th>Wake-on-Connect</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {deployments.map(d => (
                  <tr key={d._id}>
                    <td>
                      <div style={{ fontWeight:600 }}>{d.name}</div>
                      <div style={{ fontSize:11, fontFamily:'var(--font-mono)', color:'#4d6080' }}>
                        {d.providerResourceId?.length > 20 ? d.providerResourceId.slice(-12) : d.providerResourceId}
                      </div>
                    </td>
                    <td><span style={{ color: PROVIDER_COLORS[d.provider], fontWeight:600, textTransform:'uppercase', fontFamily:'var(--font-mono)', fontSize:12 }}>{d.provider}</span></td>
                    <td><span style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'#8899b4' }}>{d.region}</span></td>
                    <td><span className="badge badge-blue">{d.type}</span></td>
                    <td><span className={`badge ${STATUS_BADGE[d.status] || 'badge-blue'}`}>● {d.status}</span></td>
                    <td><span style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>{d.publicIP || '—'}</span></td>
                    <td><span style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'#f59e0b' }}>${(d.cost?.hourlyRate||0).toFixed(4)}</span></td>
                    <td><span style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--text-primary)' }}>${(d.cost?.totalCost||0).toFixed(2)}</span></td>
                    <td>
                      <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!d.autoStop?.enabled} onChange={e => handleToggleAutoStop(d._id, e.target.checked)} />
                        <span style={{ fontSize:11, color: d.autoStop?.enabled ? 'var(--green)' : 'var(--text-secondary)' }}>{d.autoStop?.enabled ? 'On' : 'Off'}</span>
                      </label>
                    </td>
                    <td>
                      {(() => {
                        const gw = wakeGateways.find(g => (g.deploymentId?._id || g.deploymentId) === d._id);
                        return (
                          <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                            <input type="checkbox" checked={!!gw} onChange={e => handleToggleWake(d, e.target.checked)} />
                            {gw ? <span style={{ fontSize:11, fontFamily:'var(--font-mono)', color:'var(--green)' }}>{HOST_IP}:{gw.listenPort}</span> : <span style={{ fontSize:11, color:'var(--text-secondary)' }}>Off</span>}
                          </label>
                        );
                      })()}
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        {d.status === 'running' && <button className="btn btn-sm btn-secondary" onClick={() => handleStop(d._id)}>Stop</button>}
                        {d.status === 'stopped'  && <button className="btn btn-sm btn-success"   onClick={() => handleStart(d._id)}>Start</button>}
                        {!['terminated'].includes(d.status) && <button className="btn btn-sm btn-danger" onClick={() => handleDestroy(d._id)}>Destroy</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🚀 New Deployment</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Deployment Name</label>
                <input className="form-control" placeholder="my-app-prod" value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })} required />
                <small style={{color:'#8899b4',fontSize:11}}>Lowercase letters, numbers and hyphens only (GCP requirement)</small>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div className="form-group">
                  <label>Cloud Provider</label>
                  <select className="form-control" value={form.provider}
                    onChange={e => setForm({ ...form, provider: e.target.value })}>
                    {PROVIDERS.map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Region</label>
                  <select className="form-control" value={form.region}
                    onChange={e => setForm({ ...form, region: e.target.value })}>
                    {regions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Workload Type</label>
                  <select className="form-control" value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value })}>
                    {['compute','container','serverless','database'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Instance Type</label>
                  <select className="form-control" value={form.instanceType}
                    onChange={e => setForm({ ...form, instanceType: e.target.value })}>
                    {(INSTANCE_TYPES[form.provider] || []).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                  <input type="checkbox" checked={form.ephemeral} onChange={e => setForm({ ...form, ephemeral: e.target.checked })} />
                  Ephemeral workload (auto-terminates)
                </label>
              </div>
              <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? '⏳ Deploying...' : 'Deploy →'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
